/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { ContractInfo } from '@/ContractConfig';
import Bootstrap, { appVersion } from '@/shared/utils/bootstrap';
import { versionCompare } from '@/shared/utils/versionCompare';
import { jumpToNewVersion } from '@/shared/utils/jumpTo';
import { NewVersionEnum } from '@/enums/newVersionRoute';
import store from '@/store';
import { ubtCustomEvent, ubtSendError } from '@/shared/utils/ubt';
import { local } from '@/shared/utils/storage';
import { DEFAULT_STATUSBAR_HEIGHT } from '@/constant';
import isSupportNativeDataStorage from '@/shared/utils/isSupportNativeDataStorage';
import { landData } from '@/shared/utils/landData';
import router from '@/router';
import URLUtils from '@/shared/utils/URLUtils';
import { callNative } from '../../shared/utils/bridge';
import platform from '../../shared/utils/platform';
import { aliOpenThirdPageWithoutInfo } from './cardPhoto';
import wechat, { checkLinkIsNotAccessDomain } from './wechat';
import type { SetStatusBarColorParams } from './types';

const HBridge = import('@hb/hbridge');

// 设置页面标题
// 哈啰出行APP，支付宝小程序；微信小程序不支持使用document.title替代；
export const setPageTitle = async (title: string):
  Promise<void> => {
  if (platform.isWechatMini) {
    document.title = title;

    return Promise.resolve();
  }

  return (await HBridge).Native.setPageTitle({ title });
};

// 设置安卓是否需要状态栏（改为调用本地bridge方法）
export const setStatusBar = async (params: SetStatusBarColorParams):
  Promise<any> => callNative({
  classMap: 'NavBar',
  method: 'setStatusBarColor',
  params,
});

/** 设置设置状态栏(兼容各平台，只在哈啰App安卓和鸿蒙设备生效) */
export const setStatusBarCompatPlatform = async (params: SetStatusBarColorParams):
  Promise<any> => {
  if (platform.isHelloApp && (platform.isAndroid || platform.isHarmony)) {
    return setStatusBar(params);
  }

  return Promise.resolve();
};

/** 获取状态栏高度
 * 哈啰app内Android 6.88.0版本开始支持
 */
export const getStatusBarHeight = async () => {
  let height = DEFAULT_STATUSBAR_HEIGHT;
  try {
    // 记账app不支持获取状态栏高度，需要排除
    if (platform.isHelloApp && !platform.isRechargeApp && !platform.isSkzhenxhApp && platform.isAndroid && versionCompare(appVersion, '6.88.0') >= 0) {
      const data = await callNative({
        classMap: 'business/finance',
        method: 'getStatusBarHeight',
      });
      height = Number(data) || DEFAULT_STATUSBAR_HEIGHT;
    }
  } catch (err) {
    ubtSendError(err as Error);
  }
  return height;
};

// 获取用户定位信息
// 哈啰出行APP，支付宝小程序；支付宝小程序；
export const getCityInfo = async ():
  Promise<NativeModel.CityInfo | NativeModel.AliCityInfo> => {
  /** 微信小程序无定位权限，不获取定位信息 */
  if (platform.isWechatMini) {
    return Promise.resolve({});
  }

  return (await HBridge).Native.getCityInfo()
    .then((data) => {
      if (platform.isAlipayMini) {
        return Promise.resolve({
          cityAdcode: (data as any).accurateAdCode || '',
          city: (data as any).city || '',
          cityCode: (data as any).cityCode || '',
          latitude: (data as any).accurateLatitude || '',
          longitude: (data as any).accurateLongitude || '',
          province: (data as any).province || '',
          country: (data as any).country || '',
        } as NativeModel.AliCityInfo);
      }
      return Promise.resolve({
        adCode: data.adCode || '',
        cityCode: data.cityCode || '',
        cityName: ((data as any).city && String((data as any).city)) || '',
        latitude: data.latitude || '',
        longitude: data.longitude || '',
      } as NativeModel.CityInfo);
    }).catch(() => Promise.resolve({}));
};

// 返回上一页,回退web栈，没有可返回，会关闭web页面
// 哈啰出行APP，支付宝小程序；支付宝小程序；
export const goBack = async (): Promise<void> => (await HBridge).Native.goBack();

// 调起设备拨打电话
// 哈啰出行APP，支付宝小程序；支付宝小程序；
export const call = async (phone: string):
  Promise<void> => (await HBridge).Native.call(phone);

/** 打开一个新的webview，左上角有关闭功能
 * @param url:打开的页面
 * @param needNewPage:是否新开页面打开
*/
export const openThirdPage = async (
  url: string,
  needNewPage?: boolean,
  /** 打开webview是否携带用户信息 */
  withNoInfo?: boolean,
):
  Promise<void> => {
  if (platform.isHelloApp) {
    let newUrl = url;
    if (store.state.base.isHybridAppMode) {
      newUrl = `${url}${url.includes('?') ? '&' : '?'}hybridAppMode=1`;
    }
    (await HBridge).Native.callNative({
      classMap: 'Third',
      method: 'openThirdWeb',
      params: {
        url: newUrl,
      },
    });
  } else if (platform.isAlipayMini && needNewPage) {
    if (withNoInfo) {
      await aliOpenThirdPageWithoutInfo(url);
    } else {
      (await HBridge).Native.callNative({
        method: 'goOperationUrlV2',
        params: url,
      });
    }
  } else if (platform.isWechatMini && (await checkLinkIsNotAccessDomain(url))) {
    jumpToNewVersion(NewVersionEnum.MINI_PROGRAMS_PROMPT, false);
  } else {
    window.location.href = url;
  }
};

/** 当前项目 H5 的线上地址前缀，传入的 URL 以此开头则视为当前项目内链 */
const CURRENT_PROJECT_BASE_URL = 'https://m.hellobike.com/AppFinanceH5Web';

/** 传入的 URL 是否属于当前项目 */
export const isTargetUrlInCurrentProject = (url: string): boolean => (
  !!url && url.startsWith(CURRENT_PROJECT_BASE_URL)
);

/**
 * 跳转目标 URL：若传入的 URL 指向当前项目则用 router，否则用 openThirdPage 打开。
 */
export const navigateToUrlIfInProject = (targetUrl: string): void => {
  if (!targetUrl || typeof targetUrl !== 'string') return;

  if (isTargetUrlInCurrentProject(targetUrl)) {
    const hash = (targetUrl.split('#')[1] || '').trim() || '/';
    const [pathPart, searchPart] = hash.indexOf('?') >= 0 ? hash.split('?') : [hash, ''];
    const path = pathPart || '/';
    const query: Record<string, string> = {};
    if (searchPart) {
      const params = new URLSearchParams(searchPart);
      params.forEach((v, k) => { query[k] = v; });
    }
    router.push({ path, query });
  } else {
    openThirdPage(targetUrl);
  }
};

/** 通过新webview打开页面 */
export const openInnerPage = async (url: string): Promise<void> => {
  if (platform.isHelloApp) {
    let newUrl = url;
    if (store.state.base.isHybridAppMode) {
      newUrl = `${url}${url.includes('?') ? '&' : '?'}hybridAppMode=1`;
    }
    if (versionCompare(appVersion, '6.3.0') >= 0) {
      callNative({
        classMap: 'business/marketing',
        method: 'openMarketingWeb',
        params: {
          url: newUrl.trim(),
        },
      }).catch(() => ({}));
    } else {
      callNative({
        classMap: 'Third',
        method: 'openThirdWeb',
        params: {
          url: newUrl.trim(),
        },
      }).catch(() => ({}));
    }
  } else if (platform.isAliMP) {
    (await HBridge).Native.callNative({
      method: 'goOperationUrlV2',
      params: url,
    });
  } else {
    window.location.href = url;
  }
};

// 直接关闭当前webview, 注意：不要在APP首页的H5中调用，会直接关闭应用
// 哈啰出行,支付宝小程序
// TODO:钉钉浏览器场景 待处理
export const closeWebView = async (): Promise<void> => {
  if (platform.isAlipayMini) {
    (await HBridge).Native.callNative({
      method: 'navigateBack',
      params: {},
    });
  } else if (platform.isWechatMini) {
    await wechat.navigateBack();
  } else {
    (await HBridge).Native.closeWebView();
  }
};
/** 独立 app 回到首页 */
export const skBackHome = async (): Promise<void> => callNative({
  classMap: 'SKAppUtils',
  method: 'skBackHome',
  params: {},
});

/** 跳转至钱包页TAB */
export const jumpToWallet = (): void => {
  window.location.href = 'hellobike://hellobike.com/wallet?index=1';
};

type AlipaySystemInfo = {
  model: string,
  storage: string,
  screenWidth: string,
  screenHeight: string,
  currentBattery: string,
  platform: string,
  system: string
}

// 获取支付宝设备信息
export const getSystemInfo = async (): Promise<NativeModel.DeviceInfo> => {
  const stystemInfo = await (await HBridge).Native.callNative({
    method: 'getSystemInfo',
    params: {},
  }) as unknown as AlipaySystemInfo;
  const {
    model,
    storage,
    screenWidth,
    screenHeight,
    currentBattery,
    platform: alipayPlatform,
    system: alipaySystem,
  } = stystemInfo || {};
  // 包装当前支付宝小程序设备信息，保证和app字段一致
  return {
    os: alipayPlatform,
    osVersion: alipaySystem,
    totalSize: storage,
    deviceModel: model,
    screenWidth,
    screenHeight,
    batteryChargeStatus: currentBattery,
    appVersion: '',
  };
};

// 平台缓存方法 Android 6.39.0版本开始支持, value 值 需要传数组，不能直接字符串，否则会存储失败
export const nativeLocalSetData = (params: { [key: string]: any }) => {
  if (isSupportNativeDataStorage()) {
    return callNative({
      classMap: 'context',
      method: 'setData',
      params,
      mannualError: true,
    }).catch((err) => {
      ubtSendError(err as Error);
      /** 若Native存储异常，则使用local兜底 */
      local.set(params.key, params.value);
    });
  }

  return Promise.resolve(local.set(params.key, params.value));
};

export const platformLocalSetData = async (params: { [key: string]: string }) => {
  if (platform.isHelloApp) {
    return callNative({
      classMap: 'context',
      method: 'setData',
      params: {
        key: params.key,
        value: [params.value],
      },
    });
  }

  return new Promise((resolve) => {
    resolve(localStorage.setItem(params.key, params.value));
  });
};

// 平台缓存取方法 Android 6.39.0版本开始支持 ios 早于6.22.0版本开始支持
export const nativeLocalGetData = (params: { [key: string]: string }) => {
  if (isSupportNativeDataStorage()) {
    return callNative({
      classMap: 'context',
      method: 'getData',
      params,
      mannualError: true,
    }).catch((err) => {
      ubtSendError(err as Error);
      /** 若Native存储异常，则使用local兜底 */
      return Promise.resolve(local.get(params.key));
    });
  }

  return Promise.resolve(local.get(params.key));
};

export const platformLocalGetData = async (params: { [key: string]: string }) => {
  if (platform.isHelloApp) {
    return callNative({
      classMap: 'context',
      method: 'getData',
      params,
    });
  }

  return new Promise((resolve) => {
    resolve(localStorage.getItem(params.key));
  });
};

// 平台移除缓存方法 Android 6.39.0版本开始支持
export const nativeLocalRemoveData = (params: { [key: string]: string }) => callNative({
  classMap: 'context',
  method: 'removeData',
  params,
}).catch((err) => {
  ubtSendError(err as Error);
  /** 若Native存储异常，则使用local兜底 */
  return Promise.resolve(local.remove(params.key));
});

// 获取通讯录中联系人信息（该方法目前已应用至臻有钱，但该方法已无实际应用场景）
// 哈啰出行APP
// 无使用场景
export const getContracts = (): Promise<{
  status: 0 | 1;
  data: ContractInfo[];
}> => callNative({
  classMap: 'business/finance',
  method: 'financeContactInfo',
});

// 获取设备信息。 5.15.0之后才支持；5.22.0加入boottime等字段
export const getDeviceInfo = (): Promise<NativeModel.DeviceInfo> => {
  let classMap = 'business/finance';
  let method = 'financeDeviceInfo';

  // 哈臻快app和记账app使用相同的Native方法
  if (platform.isRechargeApp || platform.isSkzhenxhApp) {
    classMap = 'business/sk';
    method = 'skDeviceInfo';
  }
  return callNative({
    classMap,
    method,
  });
};

// 从原生获取投放链路启动流程中的时间点位
export const skMarkLaunchTimes = (): Promise<any> => {
  const classMap = 'business/finance';
  const method = 'skMarkLaunchTimes';

  return callNative({
    classMap,
    method,
  });
};

// 从安卓客户端获取App是否首次安装打开
export const isFirstInstall = async (): Promise<boolean> => {
  const classMap = 'business/home';
  const method = 'isFirstInstall';

  if (platform.isAndroid && versionCompare(appVersion, '6.98.10') >= 0) {
    const data = await callNative({
      classMap,
      method,
    });
    return (data as { isFirstInstall: boolean }).isFirstInstall;
  }

  return false;
};

/**
 * 对投放链路启动流程中的时间点位进行埋点,
 * 只有链接的source为ads时才调用
 * 安卓哈啰App6.94.5版本开始支持
 * 获取当前页面的url, 判断是否存在source参数，如果存在source且source的值包含“toutiao”或者“guangdiantong”时才调用
 */
export const markLaunchTimes = async () => {
  try {
    const source = landData.getSouce();
    const isMatch = ['toutiao', 'guangdiantong'].some((item) => source.toLowerCase().includes(item));
    if (!isMatch) {
      return;
    }
    if (platform.isAndroid
    && platform.isEasyBike
    && versionCompare(appVersion, '6.94.5') >= 0) {
      const data = await skMarkLaunchTimes();
      const firstInstall = await isFirstInstall();
      // 分别是： 是否首次安装、App启动时间、同意隐私时间、获取url时间、开始路由时间、启动mpaas时间
      const {
        startTime, privacyTime, urlTime, routeTime, mpaasTime,
      } = data;
      // 获取当前页面的导航开始时间
      const navigateTime = window.performance.timing.navigationStart;
      // 以下是为了快速查看一部分点位间的时长而设置的，实际分析时还是需要按情况根据以上点位进行计算
      const privacyDuration = privacyTime - startTime;
      const getUrlDuration = urlTime - privacyTime;
      const routeDuration = routeTime - urlTime;
      const jumpDuration = mpaasTime - routeTime;
      const launchDuration = navigateTime - routeTime;
      const totalDuration = navigateTime - startTime;
      const url = window.location.href;
      // 在原生启动模块增加实验控制是否进入优化链路
      // webStartOp为实验参数，值为1时进入优化链路，值为0时进入原链路,-1表示未进实验链路
      const webStartOp = URLUtils.getQueryMap().webStartOp || -1;
      const extraParams = {
        firstInstall,
        startTime,
        privacyTime,
        urlTime,
        routeTime,
        mpaasTime,
        navigateTime,
        source,
        privacyDuration,
        getUrlDuration,
        routeDuration,
        jumpDuration,
        launchDuration,
        totalDuration,
        webStartOp,
        url,
      };
      ubtCustomEvent('finance_operation_ads', 'finance_operation_ads_launch_navigate_times', extraParams);
    }
  } catch (error) {
    //
  }
};

/** 用于获取app内的clinetId */
export const getPlatformDeviceInfo = async ():
  Promise<NativeModel.PlatformDeviceInfo> => ((await HBridge).Native.callNative({
  classMap: 'business/home',
  method: 'getPlatformDeviceInfo',
  params: {},
}) as unknown as NativeModel.PlatformDeviceInfo);


// 获取设备指纹
export const getDeviceFingerprintHash = (): Promise<{ [key: string]: string }> => callNative({
  classMap: 'SystemUtil',
  method: 'getDeviceFingerprintHash',
});

// 以前的获取设备
export const getOldDeviceInfo = (): Promise<{ identifier: string }> => callNative({
  classMap: 'Third',
  method: 'getDeviceInfo',
});

/** 无调用 */
/**
 * 当获取的客户端 token 判断失效的时候调用 , 清除登录信息, 会关闭当前 WebView 并提示重新登录
 * 哈啰出行 app 支付宝小程序都适用
*/
export const invalidToken = async (): Promise<void> => {
  (await HBridge).Native.invalidToken();
};

/**
 * 退出登录
 * 哈啰出行 app 支付宝小程序都适用
*/
export const logout = async (): Promise<void> => {
  // 退出登录前，清空store 的登录数据
  Bootstrap.initLoginUser(Object.create(null), Object.create(null));
  // Android app 内调用
  if (platform.isHelloApp && (platform.isAndroid || platform.isHarmony)) {
    await callNative({
      classMap: 'business/user',
      method: 'logout',
    });
  } else if (platform.isWechatMini) {
    /** 清除bridge的登录缓存 */
    (await HBridge).Native.logout();

    /** 通知小程序清除token */
    await wechat.notifyLogout();
    /** 退出返回上一页 */
    await wechat.navigateBack();
  } else {
    (await HBridge).Native.logout();
    if (platform.isSkApp) {
      closeWebView();
    }
  }
};
/** 获取用户的token及key, 可用于引导用户进行登录
如果needLogin为true, 且用户没有登录, 则会弹出登录窗口 */
export const getTokenAndKeyAndLogin = async (needLogin: boolean): Promise<void> => {
  (await HBridge).Native.getTokenAndKey({
    needLogin,
  });
};

/** 设置原生标题 */
export const setNativeNavBar = (isShow: boolean): Promise<void> => {
  if (platform.isWechatMini || platform.isAlipayMini) {
    /** 兼容微信小程序，无设置原生导航栏功能的api */
    return Promise.resolve();
  }

  return callNative({
    classMap: 'NavBar',
    method: 'showNaviBar',
    params: {
      isShow,
    },
  });
};

// 检查权限，相机、相册、视频
export enum PermissionEnum {
  /** 相册权限 */
  GALLERY = 1,
  /** 相机权限 */
  TAKE_PHOTO = 2,
  /** 录音权限 */
  TAKE_VIDEO = 3,
}

export const checkPermission = async (
  type: PermissionEnum) => {
  if ((platform.isAndroid || platform.isHarmony) && versionCompare(store.state.base.deviceInfo.appVersion, '6.47.0') < 0) {
    return (await HBridge).Native.checkPermission({ type });
  }

  return { status: true };
};
